import Auth from "../../../../Auth.js";
import { createEntityImageApi } from "../../../EntityImage/lib/entityImageApi";
import type { GenerateCandidateResponse } from "../../../EntityImage/lib/entityImageApi";
import type { EntityImageListItem, ReferenceImagePayload, StylePresetOption } from "../../../EntityImage/lib/types";

export type { ReferenceImagePayload, StylePresetOption } from "../../../EntityImage/lib/types";
export type {
  DeleteImageResponse,
  GenerateCandidateResponse,
  ListEntityImagesResponse as ListPortraitsResponse,
  SaveImageResponse,
  SetCoverImageResponse,
  StylePresetsResponse,
} from "../../../EntityImage/lib/entityImageApi";
export type CharacterPortraitListItem = EntityImageListItem;

export const characterImageApi = createEntityImageApi("/api/characters");

// Mirrors NO_STYLE_PRESET_ID in src/services/characterImage.ts; redeclared since that file
// pulls in server-only deps (Prisma, S3) that shouldn't reach the client bundle.
export const NO_STYLE_PRESET_ID = "none";

export interface SummarizeResponse {
  success: true;
  subject: string;
  mood: string | null;
  /** A user-described setting to place the character in, or null for a plain background. */
  background: string | null;
  stylePresets: StylePresetOption[];
}

export const saveGeneratedCharacterPortrait = characterImageApi.saveGenerated;
export const listCharacterImageStylePresets = (characterId: string) => characterImageApi.listStylePresets(characterId);
export const uploadCharacterPortrait = characterImageApi.upload;
export const listCharacterPortraits = characterImageApi.list;
export const deleteCharacterPortrait = characterImageApi.remove;
export const setCoverCharacterPortrait = characterImageApi.setCover;

export function summarizeCharacterVisuals(
  characterId: string,
  userDescription: string,
  traits: { physicalDescription?: string | null; pronouns?: string | null } = {},
  hasReferenceImage: boolean = false,
): Promise<SummarizeResponse> {
  return Auth.fetchWithIdToken(characterImageApi.imagesUrl(characterId, "/summarize"), {
    userDescription,
    physicalDescription: traits.physicalDescription ?? undefined,
    pronouns: traits.pronouns ?? undefined,
    hasReferenceImage,
  });
}

export function generateCharacterPortrait(
  characterId: string,
  params: {
    subject: string;
    mood: string | null;
    background?: string | null;
    stylePresetId: string;
    stylePresetText?: string;
    referenceImage?: ReferenceImagePayload | null;
    referenceImageGuidance?: string;
    userDescription?: string;
    // Shared across a batch's tile requests so the ledger groups their charges into one line —
    // see mergeEntityImageBatchEntries.
    batchId?: string;
  },
): Promise<GenerateCandidateResponse> {
  return Auth.fetchWithIdToken(characterImageApi.imagesUrl(characterId, "/generate"), {
    subject: params.subject,
    mood: params.mood,
    background: params.background ?? undefined,
    stylePresetId: params.stylePresetId,
    stylePresetText: params.stylePresetText ?? undefined,
    referenceImage: params.referenceImage ?? undefined,
    referenceImageGuidance: params.referenceImageGuidance ?? undefined,
    userDescription: params.userDescription || undefined,
    batchId: params.batchId ?? undefined,
  });
}
